import { Injectable } from '@angular/core';
import { FREQUENCIES, Frequency } from '../../models/frequencies';

interface ErrorLike {
  response?: {
    data?:
      | {
          message?: string;
        }
      | string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private frequenciesMap?: Map<number, Frequency>;

  private static readonly MONTH_NAMES = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec'
  ];

  private static readonly EMAIL_REGEX = /^[\w-.]+@irobot\.com$/;

  constructor() {}

  public getFrequencies(): Map<number, Frequency> {
    if (!this.frequenciesMap) {
      this.frequenciesMap = new Map(FREQUENCIES.map((frequency) => [frequency.value, frequency] as const));
    }

    return this.frequenciesMap;
  }

  public getMatchedFrequencyLabels(frequencyValues: number[] | null | undefined): string[] {
    if (!frequencyValues?.length) {
      return [];
    }

    const frequencies = this.getFrequencies();

    return frequencyValues.map((value) => frequencies.get(value)?.label).filter((label): label is string => !!label);
  }

  public getMatchedFrequencyLabel(frequencyValue: number): string | null {
    return this.getFrequencies().get(frequencyValue)?.label ?? null;
  }

  public convertUnixTime_global(epoch: number | null | undefined): string | null {
    if (!epoch) {
      return null;
    }

    const date = new Date(epoch * 1000);
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const hours24 = date.getHours();
    const hours12 = ((hours24 + 11) % 12) + 1;
    const meridiem = hours24 >= 12 ? 'PM' : 'AM';
    const month = UtilityService.MONTH_NAMES[date.getMonth()];

    return `${date.getFullYear()}/${month}/${date.getDate()}   ${hours12}:${minutes} ${meridiem}`;
  }

  public convertErrorToMessage(error: ErrorLike | null | undefined): string {
    const data = error?.response?.data;

    if (typeof data === 'string') {
      return data;
    }

    if (data?.message) {
      return data.message;
    }

    return 'Error!';
  }

  public createRandomMockID(): string {
    return `mock-${Math.random().toString(36).slice(2)}`;
  }

  public formatDictToObject<T extends object>(data: Record<string, T>): ({ id: string } & T)[] {
    return Object.entries(data).map(([id, values]) => ({
      id,
      ...values
    }));
  }

  public verifyEmail(email: string): boolean {
    return email.length === 0 || UtilityService.EMAIL_REGEX.test(email);
  }
}
